import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { expenseLimitsAPI } from '../../services/api';
import { ExpenseLimit } from '../../types/expense';

export const ExpenseLimitCard: React.FC = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [monthlyLimit, setMonthlyLimit] = useState<number | null>(null);
  const [yearlyLimit, setYearlyLimit] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') return;

    const fetchLimits = async () => {
      try {
        const response = await expenseLimitsAPI.getAll();
        const limits = response.data.expense_limits || [];
        const suppliesLimits = limits.filter((l: ExpenseLimit) => l.category === 'supplies');

        const monthly = suppliesLimits.find((l: ExpenseLimit) => l.period === 'monthly');
        const yearly = suppliesLimits.find((l: ExpenseLimit) => l.period === 'yearly');

        setMonthlyLimit(monthly?.limit_amount ?? null);
        setYearlyLimit(yearly?.limit_amount ?? null);
      } catch (error) {
        console.error('Failed to fetch expense limits:', error);
      }
    };

    fetchLimits();
  }, [user?.role]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const limits: Partial<ExpenseLimit>[] = [];

      if (monthlyLimit !== null) {
        limits.push({
          category: 'supplies',
          period: 'monthly',
          limit_amount: monthlyLimit,
          active: true,
        });
      }
      if (yearlyLimit !== null) {
        limits.push({
          category: 'supplies',
          period: 'yearly',
          limit_amount: yearlyLimit,
          active: true,
        });
      }

      await expenseLimitsAPI.upsert(limits);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save expense limits:', error);
    } finally {
      setLoading(false);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 rounded-2xl'
    : 'bg-white border border-slate-200 rounded-2xl shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent'
    : 'w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent';

  // Only render for admins
  if (user?.role !== 'admin') return null;

  return (
    <div className={`${cardClass} overflow-hidden`}>
      <div
        className={`px-6 py-4 border-b ${isDark ? 'border-slate-700/50 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}
      >
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Limites de Dépenses - Fournitures de Bureau
        </h2>
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Configurez les limites mensuelles et annuelles pour les dépenses en fournitures
        </p>
      </div>

      <div className="p-6 space-y-4">
        <div className="max-w-md">
          <label
            htmlFor="monthly-limit"
            className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
          >
            Limite mensuelle ($)
          </label>
          <input
            id="monthly-limit"
            type="number"
            min="0"
            step="0.01"
            value={monthlyLimit ?? ''}
            onChange={(e) => setMonthlyLimit(e.target.value ? parseFloat(e.target.value) : null)}
            className={inputClass}
            placeholder="Aucune limite"
          />
        </div>

        <div className="max-w-md">
          <label
            htmlFor="yearly-limit"
            className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
          >
            Limite annuelle ($)
          </label>
          <input
            id="yearly-limit"
            type="number"
            min="0"
            step="0.01"
            value={yearlyLimit ?? ''}
            onChange={(e) => setYearlyLimit(e.target.value ? parseFloat(e.target.value) : null)}
            className={inputClass}
            placeholder="Aucune limite"
          />
        </div>
      </div>

      <div
        className={`px-6 py-4 border-t ${isDark ? 'border-slate-700/50 bg-slate-700/10' : 'border-slate-200 bg-slate-50'}`}
      >
        <div className="flex items-center justify-between">
          <div>
            {saved && (
              <div className={`text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                ✓ Limites sauvegardées
              </div>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
};
